// src/components/molecules/SubmissionDetails/ContributionAmountPicker.js
import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  CONTRIBUTION_INCREMENT,
  MIN_CONTRIBUTION,
  contributionChips,
  normalizeContribution,
} from "../../../utils/purchaseAmount";

const ContributionAmountPicker = ({ amountUsd, maxAmount, onAmountChange, theme }) => {
  // Raw text while the field is being edited. The committed amount is the
  // parent's `amountUsd`; this only exists so intermediate keystrokes survive.
  const [draft, setDraft] = useState(String(amountUsd));

  useEffect(() => {
    setDraft(String(amountUsd));
  }, [amountUsd]);

  const chips = contributionChips(maxAmount);

  const handleChange = (event) => {
    setDraft(event.target.value.replace(/[^0-9]/g, ""));
  };

  const handleBlur = () => {
    const normalized = normalizeContribution(draft, maxAmount);
    setDraft(String(normalized));
    onAmountChange(normalized);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="body2"
        sx={{ fontFamily: "basic-sans", fontWeight: 700, mb: 1 }}
      >
        Choose your contribution
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
        {chips.map((chip) => {
          const isSelected = chip.amount === amountUsd;
          return (
            <Button
              key={chip.label}
              aria-pressed={isSelected}
              onClick={() => onAmountChange(chip.amount)}
              sx={{
                fontFamily: "basic-sans",
                textTransform: "none",
                borderRadius: "50px",
                padding: "4px 16px",
                boxShadow: "none",
                border: `1px solid ${isSelected ? theme.colors.green : theme.colors.palePurple}`,
                backgroundColor: isSelected ? theme.colors.green : "transparent",
                color: isSelected ? theme.colors.white : theme.colors.darkGray,
                "&:hover": {
                  backgroundColor: isSelected ? theme.colors.green : theme.colors.palePurple,
                  boxShadow: "none",
                },
              }}
            >
              {chip.label}
            </Button>
          );
        })}
      </Box>

      <Typography
        variant="body2"
        sx={{ fontFamily: "basic-sans", color: theme.colors.darkGray, mb: 0.5 }}
      >
        Or enter your own
      </Typography>

      {/* The dollar sign sits inside the field's border, unlike the old
          absolutely-positioned prefix that hung outside it. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: theme.colors.white,
          borderRadius: "5px",
          padding: "5px 10px",
        }}
      >
        <Typography component="span" sx={{ color: theme.colors.darkGray, mr: 0.5 }}>
          $
        </Typography>
        <input
          type="text"
          aria-label="Contribution amount in dollars"
          value={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          inputMode="numeric"
          pattern="[0-9]*"
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            fontFamily: "basic-sans",
            fontSize: "1em",
            color: "black",
          }}
        />
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontFamily: "basic-sans",
          color: theme.colors.darkGray,
          mt: 0.5,
        }}
      >
        {`Multiples of $${CONTRIBUTION_INCREMENT} · min $${MIN_CONTRIBUTION} · max $${maxAmount}`}
      </Typography>
    </Box>
  );
};

export default ContributionAmountPicker;
